import type { CSSProperties } from "react";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";

/**
 * Outline colors for profile pictures shown anywhere in IAPP (the Nav Bar / side bar
 * avatar is deliberately excluded and keeps its own treatment).
 *
 * Clients and advisors get a neutral gray ring; IAPP case managers get the IAPP blue
 * ring so the "who owns this" avatar reads at a glance.
 */
export const PROFILE_PIC_BORDER_GRAY = "rgb(203, 213, 225)";
/**
 * IAPP blue comes in a light/dark pair: the navy is unreadable against the dark theme's
 * `#071426` surfaces, so dark mode uses the brighter IAPP blue instead. Prefer
 * `getCaseManagerBorderColor()` over either constant so the ring follows the theme.
 */
export const PROFILE_PIC_BORDER_IAPP = "#004781";
export const PROFILE_PIC_BORDER_IAPP_DARK = "#1581ED";
export const PROFILE_PIC_BORDER_WIDTH = "1px";

const isDarkTheme = (): boolean => getSessionStore("globalTheme") === THEME.DARK;

/** The case-manager ring color for the active theme. */
export const getCaseManagerBorderColor = (isDark: boolean = isDarkTheme()): string =>
  isDark ? PROFILE_PIC_BORDER_IAPP_DARK : PROFILE_PIC_BORDER_IAPP;

/**
 * IAPP case managers come through the API as `admin` (and background automation as
 * `system`); everything else — `agent` (advisor), `contact` (client), `assistant` —
 * is treated as a regular user.
 */
export const isCaseManagerUserType = (userType?: string | null): boolean => {
  const normalized = (userType ?? "").trim().toLowerCase();
  return normalized === "admin" || normalized === "system";
};

/** Ring color for a profile picture belonging to a user of `userType`. */
export const getProfilePicBorderColor = (userType?: string | null): string =>
  isCaseManagerUserType(userType) ? getCaseManagerBorderColor() : PROFILE_PIC_BORDER_GRAY;

/**
 * Some screens only know the person's plan role ("CASE MANAGER", "ADMIN", "PRIMARY",
 * "ADVISOR", "INSURED", ...) rather than their account type.
 */
export const isCaseManagerRole = (role?: string | null): boolean => {
  const normalized = (role ?? "").trim().toUpperCase();
  return normalized === "CASE MANAGER" || normalized === "ADMIN";
};

/** Ring color for a profile picture identified by plan role instead of user type. */
export const getProfilePicBorderColorByRole = (role?: string | null): string =>
  isCaseManagerRole(role) ? getCaseManagerBorderColor() : PROFILE_PIC_BORDER_GRAY;

/** Inline style for a profile picture ring, ready to spread onto an avatar. */
export const getProfilePicBorderStyle = (userType?: string | null): CSSProperties => ({
  border: `${PROFILE_PIC_BORDER_WIDTH} solid ${getProfilePicBorderColor(userType)}`,
});

/** Same as `getProfilePicBorderStyle`, keyed off the plan role instead of user type. */
export const getProfilePicBorderStyleForRole = (role?: string | null): CSSProperties => ({
  border: `${PROFILE_PIC_BORDER_WIDTH} solid ${getProfilePicBorderColorByRole(role)}`,
});
